import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import { useCharity } from "@shared/modules/events/components/donations/hooks/useCharity";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import { enums } from "@shared/constants";
import useDebounce from "@shared/hooks/useDebounce";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { AnalyticsService } from "@shared/services/analytics";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useTranslation } from "@shared/utils/internationalization";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import { NumericFormatProps } from "@ui-kit/form/NumericFormat/NumericFormat.web";
import { useEffect, useRef, useState } from "react";
import DonationsCauseDetails from "../DonationsCauseDetails/DonationsCauseDetails.web";
import DonationsFootnotes from "../DonationsFootnotes/DonationsFootnotes.web";
import DonationsHeader from "../DonationsHeader/DonationsHeader.web";
import DonationsInput from "../DonationsInput/DonationsInput.web";
import { DonationsSkeleton } from "../DonationsSkeleton/DonationsSkeleton";

const notifier = NotifierFactory.createNotifier();

type DonationsContainerProps = {
  causeId: string;
  event: PublicBaseEventSerializer | VenueBasedEventSerializer;
  isBasketUpdating: boolean;
  hideTitle?: boolean;
  useDetailedCard?: boolean;
};

export default function DonationsContainer({
  causeId,
  event,
  isBasketUpdating,
  hideTitle = false,
  useDetailedCard = true,
}: DonationsContainerProps) {
  const scrollMargin = useDetailPageScrollMargin();
  const { t } = useTranslation("common");

  const basketService = useBasketService();

  const { itemGroups, modifyItems, modifyItemGroup, addItems, removeItems } =
    basketService;

  const { isLoading, isError, charityCurrency, cause } = useCharity(
    causeId,
    event
  );

  const donationInBasket = itemGroups?.find(
    (ig) =>
      ig.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id &&
      ig.charity_id === causeId
  );

  const donationInBasketKey = parseInt(
    // When donation_amount is returned by the API, it is a string.
    // Our types for donation_amount is a number, typed after the request payload to the API.
    (donationInBasket?.donation_amount as unknown as string) ?? "0.00",
    10
  )?.toString() as keyof typeof enums.DONATION_AMOUNT_MAP;

  // This controls the quantity amount requested to the API, changed by this component only.
  const [amountRequested, setAmountRequested] = useState<{ amountKey: string }>(
    {
      amountKey: donationInBasketKey,
    }
  );
  const [debouncedAmountRequested] = useDebounce(amountRequested, 600);

  // This controls the quantity amount displayed to the user, changed by this component and by the basket.
  const [amountSelectedDisplay, setAmountSelectedDisplay] =
    useState<string>("");

  const [customDonationAmt, setCustomDonationAmt] =
    useState<NumericFormatProps>({
      value: donationInBasket?.donation_amount?.toString() ?? "0.00",
      formattedValue: `$${donationInBasket?.donation_amount}`,
      floatValue: parseFloat(
        // When donation_amount is returned by the API, it is a string.
        // Our types for donation_amount is a number, typed after the request payload to the API.
        (donationInBasket?.donation_amount as unknown as string) ?? "0.00"
      ),
    });
  const customDonationInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Change the displayed quantity when the basket quantity changes.
    setAmountSelectedDisplay(donationInBasketKey);
  }, [donationInBasketKey]);

  useEffect(() => {
    // Change the displayed quantity when this component requests a change.
    setAmountSelectedDisplay(amountRequested.amountKey);
  }, [debouncedAmountRequested]); // eslint-disable-line

  const componentDidMount = useRef(false);
  const allowRequest = useRef(true);

  const handleDonationUpdate = (
    defaultDonationAmtObj: { key: string; amount: number } | undefined,
    useCustomDonationAmt = false,
    customDonationAmt = 0
  ) => {
    if (
      defaultDonationAmtObj?.amount === 0 ||
      (useCustomDonationAmt && customDonationAmt === 0)
    ) {
      const donationItemGroup = itemGroups.find(
        (ig) => ig.id === donationInBasket?.id
      );
      if (donationItemGroup) {
        removeItems(donationItemGroup);
        UserInteractionLogger.addToCart({
          item: {
            item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
            donation_amount: 0,
          },
          basket: basketService,
        });
      }
      return;
    }
    const requestPromise = modifyItemGroup(
      {
        itemGroupId: donationInBasket?.id,
        fieldsToUpdate: {
          donation_amount: useCustomDonationAmt
            ? customDonationAmt
            : defaultDonationAmtObj?.amount ?? 0,
        },
      },
      { shouldPersist: true }
    );

    requestPromise
      .then(() => {
        UserInteractionLogger.addToCart({
          item: {
            item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
            donation_amount: defaultDonationAmtObj?.amount ?? 0,
          },
          basket: basketService,
        });
      })
      .catch(() => {
        // On error, revert displayed amount to basket amount
        setAmountSelectedDisplay(donationInBasketKey);
        allowRequest.current = false;
        setAmountRequested({ amountKey: donationInBasketKey });
      });
  };

  const handleNewDonation = (
    defaultDonationAmtObj: { key: string; amount: number } | undefined,
    useCustomDonationAmt = false,
    customDonationAmt = 0
  ) => {
    if (
      defaultDonationAmtObj?.amount === 0 ||
      (useCustomDonationAmt && customDonationAmt === 0)
    ) {
      return;
    }
    const requestPromise = addItems(
      {
        item: {
          id: causeId,
          refno: causeId,
          donation_amount: useCustomDonationAmt
            ? customDonationAmt
            : defaultDonationAmtObj?.amount ?? 0,
        },
        parent: donationInBasket?.parent,
        quantity: 1,
      },
      {}
    )
      .then(() => {
        UserInteractionLogger.addToCart({
          item: {
            item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
            donation_amount: defaultDonationAmtObj?.amount ?? 0,
          },
          basket: basketService,
        });
      })
      .catch((err) => {
        notifier.error(
          err?.response?.data?.detail ??
            t("Something went wrong, please try again later")
        );
        throw err;
      });

    requestPromise.catch(() => {
      // On error, revert displayed amount to basket amount
      setAmountSelectedDisplay(donationInBasketKey);
      allowRequest.current = false;
      setAmountRequested({ amountKey: donationInBasketKey });
    });
  };
  useEffect(() => {
    if (!componentDidMount.current) {
      // do nothing on the first component render
      componentDidMount.current = true;
      return;
    }

    if (allowRequest.current) {
      setCustomDonationAmt({
        value: "",
        formattedValue: "",
        floatValue: undefined,
      });

      const defaultDonationAmtObj = enums.DONATION_AMOUNT_MAP_BY_LIST.find(
        (amt) => amt.key === debouncedAmountRequested.amountKey
      );

      if (donationInBasket) {
        handleDonationUpdate(defaultDonationAmtObj);
        return;
      }
      handleNewDonation(defaultDonationAmtObj);
    } else {
      allowRequest.current = true;
    }
  }, [debouncedAmountRequested]); //eslint-disable-line

  const customRequestDonation = () => {
    if (customDonationAmt.value && customDonationAmt.value !== "0.00") {
      // Use a button click since we lack custom interaction types
      AnalyticsService.buttonClick({
        label: "donation",
        custom_params: {
          datum: "selection",
          type: "custom",
          amount: customDonationAmt.floatValue,
          suggested: false,
        },
      });

      const customDonationAmtKey =
        customDonationAmt.value as keyof typeof enums.DONATION_AMOUNT_MAP;

      if (donationInBasket) {
        if (customDonationAmt.floatValue === 0) {
          const donationItemGroup = itemGroups.find(
            (ig) => ig.id === donationInBasket.id
          );
          if (donationItemGroup) {
            removeItems(donationItemGroup);
          }
          return;
        }

        const requestPromise = modifyItemGroup(
          {
            itemGroupId: donationInBasket.id,
            fieldsToUpdate: {
              donation_amount: customDonationAmt.floatValue ?? 0,
            },
          },
          { shouldPersist: true }
        );

        requestPromise
          .then(() => {
            UserInteractionLogger.addToCart({
              item: {
                item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
                donation_amount: customDonationAmt.floatValue ?? 0,
              },
              basket: basketService,
            });
          })
          .catch(() => {
            // On error, revert displayed amount to basket amount
            setAmountSelectedDisplay(donationInBasketKey);
            allowRequest.current = false;
            setAmountRequested({ amountKey: donationInBasketKey });
          });

        return;
      }

      const requestPromise = modifyItems(
        {
          item: {
            id: causeId,
            refno: causeId,
            donation_amount: customDonationAmt.floatValue ?? 0,
          },
          quantity: 1,
        },
        {}
      ).catch((err) => {
        notifier.error(
          err?.response?.data?.detail ??
            t("Something went wrong, please try again later")
        );
        throw err;
      });

      requestPromise
        .then(() => {
          // If custom amount is a default donation amount, reject custom amount and select that instead
          if (enums.DONATION_AMOUNT_MAP[customDonationAmtKey]) {
            setCustomDonationAmt({
              value: "",
              formattedValue: "",
              floatValue: undefined,
            });
            customDonationInputRef?.current?.blur();
          }
          UserInteractionLogger.addToCart({
            item: {
              item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
              donation_amount: customDonationAmt.floatValue ?? 0,
            },
            basket: basketService,
          });
        })
        // Intentionally ignore errors here,
        .catch((_) => _);
    }
  };

  const handleAmountSelect = (amountKey: string) => {
    setAmountRequested({ amountKey });
  };

  const handleCustomAmountChange = (value: string) => {
    const floatValue = value ? parseFloat(value) : undefined;

    setCustomDonationAmt({
      value,
      formattedValue: floatValue ? `$${floatValue.toFixed(2)}` : "",
      floatValue,
    });
  };

  if (!cause) return <DonationsContainer.Skeleton />;

  const content = (
    <Track.Impression
      trackOnce
      analytics={{
        label: "addons",
        custom_params: {
          datum: "detail_view",
          type: "donations",
          charity_type: cause.charity_type,
          id: cause.id,
        },
      }}
    >
      <Box
        id="donations"
        width="100%"
        maxWidth="100%"
        // TODO: Add scrollMarginTop prop to Box component
        // eslint-disable-next-line showpass-custom/prefer-semantic-props
        css={{ scrollMarginTop: scrollMargin }}
      >
        <Flex justifyContent="space-between" direction="column">
          <DonationsContainer.Header hideTitle={hideTitle} />
          <DonationsContainer.CauseDetails
            causeName={cause?.name ?? ""}
            isLoading={isLoading}
            isError={isError}
            donationsVerbiage={event.donation_verbiage}
            charityRefNo={cause.refno}
            charityWebsiteUrl={cause.website_url}
          />
          <DonationsContainer.Input
            isBasketUpdating={isBasketUpdating}
            isLoading={isLoading}
            isError={isError}
            amountSelectedDisplay={amountSelectedDisplay}
            onAmountSelect={handleAmountSelect}
            customDonationAmt={customDonationAmt}
            onCustomAmountChange={handleCustomAmountChange}
            customDonationInputRef={customDonationInputRef}
            charityCurrency={charityCurrency}
            customRequestDonation={customRequestDonation}
            defaultDonationAmount={event.donation_default_amount}
          />
          <DonationsContainer.Footnotes />
        </Flex>
      </Box>
    </Track.Impression>
  );

  // On checkout, we don't want to use the detailed card container in order to match the expected design
  // Otherwise, we want to use the detailed card container (for example, in attraction items tab)
  return useDetailedCard ? (
    <DonationsContainer.DetailedCardContainer p="xl">
      {content}
    </DonationsContainer.DetailedCardContainer>
  ) : (
    content
  );
}
DonationsContainer.DetailedCardContainer = DetailCardContainer;
DonationsContainer.Header = DonationsHeader;
DonationsContainer.CauseDetails = DonationsCauseDetails;
DonationsContainer.Input = DonationsInput;
DonationsContainer.Footnotes = DonationsFootnotes;
DonationsContainer.CauseDetails = DonationsCauseDetails;
DonationsContainer.Skeleton = DonationsSkeleton;
